import {
  BiggerCriminalDetail,
  CriminalDetail,
} from "../interfaces/criminalDetail";
import { fetchWithAuth } from "./fetchWithAuth";

const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const getCriminalDetailByID = async (
  criminalDetailID: number,
): Promise<CriminalDetail> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CriminalDetail/GetCriminalDetailByID/${criminalDetailID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Criminal Detail ID ${criminalDetailID} was not found.`);
    }

    const errorText = await response.text();
    console.error("Criminal Detail Fetch Error:", errorText);
    throw new Error("Failed to fetch criminal detail");
  }

  const criminalDetail: CriminalDetail = await response.json();
  return criminalDetail;
};

export const getCriminalDetailsByCriminalOrderID = async (
  criminalOrderID: number,
): Promise<CriminalDetail[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CriminalDetail/GetCriminalDetailsByCriminalOrderID/${criminalOrderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Criminal Details Fetch Error:", errorText);
    throw new Error("Failed to fetch criminal details");
  }

  const criminalDetails: CriminalDetail[] = await response.json();
  return criminalDetails;
};

//initiated but not received
export const getPendingCriminalDetails = async (): Promise<
  BiggerCriminalDetail[]
> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CriminalDetail/GetPendingCriminalDetails`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Pending Details Fetch Error:", errorText);
    throw new Error("Failed to fetch pending criminal details");
  }

  const pendingDetails: BiggerCriminalDetail[] = await response.json();
  return pendingDetails;
};

//received but not admin reviewed
export const getCompletedCriminalDetails = async (): Promise<
  BiggerCriminalDetail[]
> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CriminalDetail/GetCompletedCriminalDetails`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Completed Details Fetch Error:", errorText);
    throw new Error("Failed to fetch completed criminal details");
  }

  const completedDetails: BiggerCriminalDetail[] = await response.json();
  return completedDetails;
};

export const updateCriminalDetail = async (
  criminalDetail: CriminalDetail,
): Promise<CriminalDetail> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CriminalDetail/UpdateCriminalDetail`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(criminalDetail),
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Criminal Detail was not found to update.`);
    }

    const errorText = await response.text();
    console.error("Update Criminal Detail Error:", errorText);
    throw new Error("Failed to save criminal detail.");
  }

  const updatedDetail: CriminalDetail = await response.json();
  return updatedDetail;
};

export const createCriminalDetail = async (
  criminalDetailCreate: CriminalDetail,
): Promise<CriminalDetail> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CriminalDetail/CreateCriminalDetail`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(criminalDetailCreate),
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Create Criminal Detail Error:", errorText);
    throw new Error("Failed to create criminal detail.");
  }

  const criminalDetail: CriminalDetail = await response.json();
  return criminalDetail;
};

export const uploadReceivedForm = async (
  file: File,
  criminalDetailID: number,
): Promise<CriminalDetail> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(
    `${apiBaseUrl}/api/CriminalDetail/UploadReceivedForm?criminalDetailID=${criminalDetailID}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Criminal Detail ID ${criminalDetailID} was not found.`);
    }

    const errorText = await response.text();
    console.error("Upload Form Error:", errorText);
    throw new Error("Failed to upload received form.");
  }

  const criminalDetail: CriminalDetail = await response.json();
  return criminalDetail;
};

export const deleteCriminalDetail = async (
  criminalDetailID: number,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CriminalDetail/DeleteCriminalDetail/${criminalDetailID}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Criminal Detail ID ${criminalDetailID} was not found.`);
    }

    const errorText = await response.text();
    console.error("Delete Criminal Detail Error:", errorText);
    throw new Error(
      `Failed to delete criminal detail with ID ${criminalDetailID}.`,
    );
  }
};

export const deleteCriminalDetailForm = async (
  criminalDetailID: number,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/CriminalDetail/DeleteCriminalDetailForm/${criminalDetailID}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Criminal Detail ID ${criminalDetailID} was not found.`);
    }

    const errorText = await response.text();
    console.error("Delete Form Error:", errorText);
    throw new Error(
      `Failed to delete criminal detail form with ID ${criminalDetailID}.`,
    );
  }
};
